import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export type DocumentRelatedTo = {entity: string, id: string};
class Document extends Base implements EntityType {
    documentId: uuid;
    contentType?: string | null;
    fileName: string;
    fileSize: number;
    local: boolean;
    hash?: string | null;
    plainText?: string | null;
    pii?: any | null;
    buffer?: string | null;
    service?: string | null;
    metadata?: any | null;
    private: boolean;
    version?: number | null;
    protocolVersion: number;
    uri?: string | null;
    uriExpires?: Date | null;
    fileHandle?: any | null;
    nameChange: boolean;
    newFileName?: string | null;
    originalName?: string | null;
    encoded: boolean;
    originalHash?: string | null;
    tags: uuid[];
    relatedTo: DocumentRelatedTo[];
    privateToUser: boolean;
    encrypted: boolean;
    constructor(doc: typeof Document | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            documentId: genUUID(),
            fileName: null,
            local: false,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "document";
        // @ts-ignore
        this.documentId = doc._documentId || doc.documentId || doc.documentid || genUUID();
        // @ts-ignore
        this.contentType = doc._contentType || doc.contentType || doc.contenttype || null;
        // @ts-ignore
        this.fileName = doc._fileName || doc.fileName || doc.filename || null;
        // @ts-ignore
        this.fileSize = util.coalesce(util.parseInt(doc._fileSize), util.parseInt(doc.fileSize), util.parseInt(doc.filesize), 0);
        // @ts-ignore
        this.local = util.defaultBoolean([doc.local, doc._local, doc.local, false]);
        // @ts-ignore
        this.hash = doc._hash || doc.hash || null;
        // @ts-ignore
        this.plainText = doc._plainText || doc.plainText || doc.plaintext || null;
        // @ts-ignore
        this.pii = doc._pii || doc.pii || {};
        // @ts-ignore
        this.buffer = doc._buffer || doc.buffer || null;
        // @ts-ignore
        this.service = doc._service || doc.service || null;
        // @ts-ignore
        this.metadata = doc._metadata || doc.metadata || {};
        // @ts-ignore
        this.private = util.defaultBoolean([doc.private, doc._private, doc.private, false]);
        // @ts-ignore
        this.version = util.coalesce(util.parseInt(doc._version), util.parseInt(doc.version), util.parseInt(doc.version), null);
        // @ts-ignore
        this.protocolVersion = util.coalesce(util.parseInt(doc._protocolVersion), util.parseInt(doc.protocolVersion), util.parseInt(doc.protocolversion), 2);
        // @ts-ignore
        this.uri = doc._uri || doc.uri || null;
        // @ts-ignore
        this.uriExpires = DateUtil.timestampOnly(doc._uriExpires) || DateUtil.timestampOnly(doc.uriExpires) || DateUtil.timestampOnly(doc.uriexpires) || null;
        // @ts-ignore
        this.fileHandle = doc._fileHandle || doc.fileHandle || doc.filehandle || {};
        // @ts-ignore
        this.nameChange = util.defaultBoolean([doc.nameChange, doc._nameChange, doc.namechange, false]);
        // @ts-ignore
        this.newFileName = doc._newFileName || doc.newFileName || doc.newfilename || null;
        // @ts-ignore
        this.originalName = doc._originalName || doc.originalName || doc.originalname || null;
        // @ts-ignore
        this.encoded = util.defaultBoolean([doc.encoded, doc._encoded, doc.encoded, false]);
        // @ts-ignore
        this.originalHash = doc._originalHash || doc.originalHash || doc.originalhash || null;
        // @ts-ignore
        this.tags = doc._tags || doc.tags || [];
        // @ts-ignore
        this.relatedTo = doc._relatedTo || doc.relatedTo || doc.relatedto || [];
        // @ts-ignore
        this.privateToUser = util.defaultBoolean([doc.privateToUser, doc._privateToUser, doc.privatetouser, false]);
        // @ts-ignore
        this.encrypted = util.defaultBoolean([doc.encrypted, doc._encrypted, doc.encrypted, false]);
    }
    get id(): uuid { return this.documentId; }
    addTag(tag: uuid): uuid {
        if (!tag) throw new ValidationError('No nulls allowed in an array!');
        if (!this.tags) this.tags = [];
        this.tags.push(tag);
        return tag;
    }
    updateTag(tag: uuid) {
        if (!this.tags) this.tags = [];
        const index = this.tags.findIndex(item => item === tag);
        if (index !== -1) { this.tags[index] = tag; }
        else this.addTag(tag);
    }
    removeTag(tag: uuid) {
        if (!this.tags) return;
        let foundItems = this.tags?.filter(item => item === tag);
        if (foundItems && foundItems.length > 0) {
            this.tags = this.tags.filter(item => !(item === tag));
        }
    }
    abandonTags() { this.tags = []; }
    addRelatedTo(relatedTo: DocumentRelatedTo): DocumentRelatedTo {
        if (!relatedTo) throw new ValidationError('No nulls allowed in an array!');
        if (!this.relatedTo) this.relatedTo = [];
        this.relatedTo.push(relatedTo);
        return relatedTo;
    }
    updateRelatedTo(relatedTo: DocumentRelatedTo) {
        if (!this.relatedTo) this.relatedTo = [];
        const index = this.relatedTo.findIndex(item => item === relatedTo);
        if (index !== -1) { this.relatedTo[index] = relatedTo; }
        else this.addRelatedTo(relatedTo);
    }
    removeRelatedTo(relatedTo: DocumentRelatedTo) {
        if (!this.relatedTo) return;
        let foundItems = this.relatedTo?.filter(item => item === relatedTo);
        if (foundItems && foundItems.length > 0) {
            this.relatedTo = this.relatedTo.filter(item => !(item === relatedTo));
        }
    }
    abandonRelatedTo() { this.relatedTo = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for this document"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","comment":"Associated firm"}]; }
    static override clientOnlySecretFields: string[] = ['uri'];
    toLowerCase() { return this.documentId; }
    displayFn() { return `${this.fileName}: "${this.plainText ? this.plainText.substring(0, 100) : ''}"`; }
    static override getNonRefArrayFields(): string[] { return ["tags", "relatedTo"]; }
    static override getDefaultEntries(): Document[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'document',
    dataArea: 'Documents and Notes',
    attachedTo: {parents: ['contact', 'lead', 'matter', 'task', 'campaign', 'expense', 'invoice'], via: 'entity', onDelete: 'delete', onReplace: 'move'},
    table: 'DOCUMENT',
    comment: 'A document or note attached to a matter, lead, contact, task, campaign, or invoice. Serves a dual purpose: uploaded files (PDFs, images, Office documents, emails, etc.) stored either inline in the database buffer for small files or in remote object storage (S3) for larger ones, and freeform text notes that can be tagged, encrypted, or marked private. Includes a plain-text index for full-text search and a structured PII field (emails, phones, people, organizations, addresses) used to power conflict checks.',
    fields: {
        documentId: {type: 'uuid', required: true, pk: true, default: '__UUID__', comment: 'Unique ID for this document'},
        contentType: {type: 'string', required: false, searchable: false, comment: 'MIME type, like image/jpeg or text/plain'},
        fileName: {type: 'string', required: true, comment: 'The name of the file (for display); blank if internal note'},
        fileSize: {type: 'int', default: 0, comment: 'Size of the file in bytes'},
        local: {type: 'boolean', required: true, comment: 'Is the content stored locally?'},
        hash: {type: 'string', searchable: false, comment: 'checksum of the content, used to look for changes'},
        plainText: {type: 'string', comment: 'Plain text copy of the document, if available. Used for searches.'},
        pii: {type: 'json', searchable: false, comment: 'Structured identifying info extracted from the document {emails,phones,people,organizations,addresses}. Used to power conflict checks. Populated by the client at upload (PDFs) or by the server (Office formats, Phase 2).'},
        buffer: {type: 'string', comment: 'File content stored in the database (base64 for binary, plain text otherwise), for files at or under 5,120 bytes'},
        service: {type: 'string', searchable: false, comment: 'Where this is stored, e.g. local, S3 or Dropbox'},
        metadata: {type: 'json', searchable: false, comment: 'The S3 ETag returned after upload, used to detect content changes'},
        private: {type: 'boolean', comment: 'Notes only: is this a private note, not part of the client file?'},
        version: {type: 'int', comment: 'Version of this document'},
        protocolVersion: {type: 'int', default: 2, comment: 'Version of the protocol used to save this document'},

        // Transient properties
        uri: {type: 'string', transient: true, searchable: false, comment: 'Location of the physical file, like s3://bucketname/'},
        uriExpires: {type: 'timestamp', transient: true, comment: 'When the pre-signed uri expires'},
        fileHandle: {type: 'json', transient: true, comment: 'The file handle to be read from'},
        nameChange: {type: 'boolean', transient: true, comment: 'Is this just a name change?'},
        newFileName: {type: 'string', transient: true, comment: 'If a name change, what is the new name?'},
        originalName: {type: 'string', transient: true, comment: 'Remember the original name of the file'},
        encoded: {type: 'boolean', transient: true, comment: 'Has the content already been encoded?'},
        originalHash: {type: 'string', transient: true, comment: 'Before editing started, what was the original hash of the document text'},

        /* Needed for note-taking */

        /**
         * These are tags related to the document.  These are NOT contacts, leads, cases, etc.  These are just text tags.
         */
        tags: {
            type: 'uuid[]',
            entityType: 'lookup',
            lookupPaths: ['note:tag'],
            required: false,
            comment: 'Tags related to this note',
        },

        /**
         * A note can be
         */
        relatedTo: {
            type: 'json[]',
            jsdoc: '{entity: string, id: string}',
            entity: {
                key: 'entity',
                value: 'id',
                deleteStrategy: 'preserve' // If we get rid of the entity ID, this document goes to the ether.
            },
            comment: 'The type/uuid of the matter, campaign or lead'
        },

        /**
         * Keep the note private?
         */
        privateToUser: {type: 'boolean', comment: 'Should this note be private (only the user can see it)?'},

        /**
         * Let the database encrypt this note on the way in.
         */
        encrypted: {type: 'boolean', comment: 'Should this note be encrypted before it is stored?'},
    },
    displayFn: "`${this.fileName}: \"${this.plainText ? this.plainText.substring(0, 100) : ''}\"`",
    hooks: [
        {when: 'before', appliesTo: 'save', method: 'processDocument', async: true}
    ],
    maps: [
        {
            name: 'entityId',
            keysFn: r => r.entityId,
            presentFn: (r, k) => r.entityId === k && r.status !== 'D'
        },
        {name: 'tags', keysFn: r => r.tags, presentFn: (r, k) => r.tags?.includes(k)},
    ],
});
    }
}
export default Document;
